import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Container,
  Alert,
  CircularProgress,
  Link,
  Grid,
  FormHelperText,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../../context/AuthContext';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

// Validation schema for registration
const registerSchema = Yup.object().shape({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  email: Yup.string().email('Invalid email address').required('Email is required'),
  password: Yup.string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(/(?=.*[a-z])/, 'Must contain at least one lowercase letter')
    .matches(/(?=.*[A-Z])/, 'Must contain at least one uppercase letter')
    .matches(/(?=.*\d)/, 'Must contain at least one number'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password')], 'Passwords must match')
    .required('Confirm password is required'),
  phone: Yup.string().required('Phone number is required'),
  address: Yup.string().required('Address is required'),
  city: Yup.string().required('City is required'),
  state: Yup.string().required('State is required'),
  zipCode: Yup.string().required('ZIP code is required'),
  dateOfBirth: Yup.date().required('Date of birth is required'),
  emergencyContactName: Yup.string().required('Emergency contact name is required'),
  emergencyContactPhone: Yup.string().required('Emergency contact phone is required'),
});

const Register: React.FC = () => {
  const [alertMessage, setAlertMessage] = useState<string>('');
  const [activeStep, setActiveStep] = useState(0);
  const { register, loading } = useAuth();
  const navigate = useNavigate();

  const steps = ['Account Information', 'Personal Information', 'Emergency Contact'];

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'USA',
      dateOfBirth: '',
      emergencyContactName: '',
      emergencyContactPhone: '',
      emailNotifications: true,
    },
    validationSchema: registerSchema,
    onSubmit: async (values) => {
      setAlertMessage('');
      try {
        const { confirmPassword, ...registrationData } = values;
        await register(registrationData);
        navigate('/register-success');
      } catch (err: any) {
        setAlertMessage(err.message || 'Registration failed. Please try again.');
      }
    },
  });

  const handleNext = () => {
    if (activeStep === 0) {
      const accountFields = ['firstName', 'lastName', 'email', 'password', 'confirmPassword'];
      const hasErrors = accountFields.some(field =>
        formik.touched[field as keyof typeof formik.touched] &&
        formik.errors[field as keyof typeof formik.errors]);

      if (!hasErrors) {
        setActiveStep((prevStep) => prevStep + 1);
      } else {
        // Touch all fields to show errors
        accountFields.forEach(field =>
          formik.setFieldTouched(field, true, true));
      }
    } else if (activeStep === 1) {
      const personalFields = ['phone', 'address', 'city', 'state', 'zipCode', 'dateOfBirth'];
      const hasErrors = personalFields.some(field =>
        formik.touched[field as keyof typeof formik.touched] &&
        formik.errors[field as keyof typeof formik.errors]);

      if (!hasErrors) {
        setActiveStep((prevStep) => prevStep + 1);
      } else {
        // Touch all fields to show errors
        personalFields.forEach(field =>
          formik.setFieldTouched(field, true, true));
      }
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }} >
                <TextField
                  name="firstName"
                  label="First Name"
                  fullWidth
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                  helperText={formik.touched.firstName && formik.errors.firstName}
                  disabled={loading}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }} >
                <TextField
                  name="lastName"
                  label="Last Name"
                  fullWidth
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                  helperText={formik.touched.lastName && formik.errors.lastName}
                  disabled={loading}
                />
              </Grid>
              <Grid size={{ xs: 12 }} >
                <TextField
                  name="email"
                  label="Email Address"
                  fullWidth
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  disabled={loading}
                />
              </Grid>
              <Grid size={{ xs: 12 }} >
                <TextField
                  name="password"
                  type="password"
                  label="Password"
                  fullWidth
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.password && Boolean(formik.errors.password)}
                  helperText={formik.touched.password && formik.errors.password}
                  disabled={loading}
                />
              </Grid>
              <Grid size={{ xs: 12 }} >
                <TextField
                  name="confirmPassword"
                  type="password"
                  label="Confirm Password"
                  fullWidth
                  value={formik.values.confirmPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                  helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
                  disabled={loading}
                />
              </Grid>
            </Grid>
          </>
        );
      case 1:
        return (
          <>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }} >
                <TextField
                  name="phone"
                  label="Phone Number"
                  fullWidth
                  value={formik.values.phone}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.phone && Boolean(formik.errors.phone)}
                  helperText={formik.touched.phone && formik.errors.phone}
                  disabled={loading}
                />
              </Grid>
              <Grid size={{ xs: 12 }} >
                <TextField
                  name="address"
                  label="Address"
                  fullWidth
                  value={formik.values.address}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.address && Boolean(formik.errors.address)}
                  helperText={formik.touched.address && formik.errors.address}
                  disabled={loading}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }} >
                <TextField
                  name="city"
                  label="City"
                  fullWidth
                  value={formik.values.city}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.city && Boolean(formik.errors.city)}
                  helperText={formik.touched.city && formik.errors.city}
                  disabled={loading}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }} >
                <TextField
                  name="state"
                  label="State"
                  fullWidth
                  value={formik.values.state}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.state && Boolean(formik.errors.state)}
                  helperText={formik.touched.state && formik.errors.state}
                  disabled={loading}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }} >
                <TextField
                  name="zipCode"
                  label="ZIP Code"
                  fullWidth
                  value={formik.values.zipCode}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.zipCode && Boolean(formik.errors.zipCode)}
                  helperText={formik.touched.zipCode && formik.errors.zipCode}
                  disabled={loading}
                />
              </Grid>
              <Grid size={{ xs: 12 }} >
                <TextField
                  name="dateOfBirth"
                  label="Date of Birth"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={formik.values.dateOfBirth}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.dateOfBirth && Boolean(formik.errors.dateOfBirth)}
                  helperText={formik.touched.dateOfBirth && formik.errors.dateOfBirth}
                  disabled={loading}
                />
              </Grid>
            </Grid>
          </>
        );
      case 2:
        return (
          <>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }} >
                <TextField
                  name="emergencyContactName"
                  label="Emergency Contact Name"
                  fullWidth
                  value={formik.values.emergencyContactName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.emergencyContactName && Boolean(formik.errors.emergencyContactName)}
                  helperText={formik.touched.emergencyContactName && formik.errors.emergencyContactName}
                  disabled={loading}
                />
              </Grid>
              <Grid size={{ xs: 12 }} >
                <TextField
                  name="emergencyContactPhone"
                  label="Emergency Contact Phone"
                  fullWidth
                  value={formik.values.emergencyContactPhone}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.emergencyContactPhone && Boolean(formik.errors.emergencyContactPhone)}
                  helperText={formik.touched.emergencyContactPhone && formik.errors.emergencyContactPhone}
                  disabled={loading}
                />
              </Grid>
            </Grid>
          </>
        );
      default:
        return 'Unknown step';
    }
  };

  return (
    <Container component="main" maxWidth="md">
      <Paper elevation={3} sx={{ padding: 4, mt: 8 }}>
        <Typography component="h1" variant="h5" align="center">
          Register for Club Membership
        </Typography>

        {alertMessage && (
          <div style={{ marginBottom: '16px' }}>
            <Typography color="error">{alertMessage}</Typography>
          </div>
        )}

        <Box sx={{ mt: 3 }}>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box component="form" onSubmit={formik.handleSubmit}>
            {getStepContent(activeStep)}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                sx={{ mr: 1 }}
              >
                Back
              </Button>

              <Box sx={{ flex: '1 1 auto' }} />

              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Submit'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNext}
                >
                  Next
                </Button>
              )}
            </Box>
          </Box>
        </Box>

        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Link component={RouterLink} to="/login" variant="body2">
            Already have an account? Sign in
          </Link>
        </Box>
      </Paper>
    </Container>
  );
};

export default Register;
